import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Download, Save, Check, X, Upload, Pen, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import wmsPmCheckSheetApi, { ChecklistItem as ApiChecklistItem } from "./wmsPmCheckSheetApi";
import "./styles/documentation-page.css";

interface ChecklistItem {
  id?: number;
  description: string;
  statusOk: boolean;
  statusNotOk: boolean;
  correctiveAction: string;
}

const WMSPMCheckSheet: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('id');
  
  const [pmDate, setPmDate] = useState<string>("");
  const [make, setMake] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [rating, setRating] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("Weekly");
  const [remarks, setRemarks] = useState<string>("");
  const [engineer, setEngineer] = useState<string>("");
  const [tech, setTech] = useState<string>("");
  const [siteIncharge, setSiteIncharge] = useState<string>("");
  const [signature, setSignature] = useState<string>("");
  const [signatureMode, setSignatureMode] = useState<'draw' | 'upload'>('draw');
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [isSheetSaved, setIsSheetSaved] = useState<boolean>(false);
  const [currentSheetId, setCurrentSheetId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signatureFileInputRef = useRef<HTMLInputElement>(null);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
    {
      id: 1,
      description: "Clean the rain gauge carefully with clean cloth",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    },
    {
      id: 2,
      description: "Clean Humidity and temperature gauge with clean cloth",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    },
    {
      id: 3,
      description: "Cleaning of pyranometer",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    },
    {
      id: 4,
      description: "Check mounted clamps are proper",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    },
    {
      id: 5,
      description: "Battery condition",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    },
    {
      id: 6,
      description: "Data logger working status",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    },
    {
      id: 7,
      description: "Check all the things are properly installed with the fixture",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    },
    {
      id: 8,
      description: "Check Anemometer or Wind van Working Condition",
      statusOk: false,
      statusNotOk: false,
      correctiveAction: ""
    }
  ]);

  // Load existing sheet if ID is provided
  useEffect(() => {
    const loadSheet = async () => {
      if (sheetId) {
        setLoading(true);
        setError("");
        try {
          const response = await wmsPmCheckSheetApi.getById(parseInt(sheetId), site || undefined);
          if (response.success && response.data) {
            const data = response.data;
            setPmDate(data.pm_date);
            setMake(data.make || "");
            setLocation(data.location || "");
            setRating(data.rating || "");
            setFrequency(data.frequency || "Weekly");
            setRemarks(data.remarks || "");
            setEngineer(data.engineer || "");
            setTech(data.tech || "");
            setSiteIncharge(data.site_incharge_name || "");
            setSignature(data.signature_image || "");
            setSignatureMode((data.signature_type as 'draw' | 'upload') || 'draw');
            setCurrentSheetId(data.id || null);
            setIsSheetSaved(true);
            
            // Load checklist items
            if (data.checklist_items && data.checklist_items.length > 0) {
              const items: ChecklistItem[] = data.checklist_items.map((item: ApiChecklistItem, index: number) => ({
                id: item.id || index + 1,
                description: item.description,
                statusOk: item.status_ok,
                statusNotOk: item.status_not_ok,
                correctiveAction: item.corrective_action || "",
              }));
              setChecklistItems(items);
            }
            
            // Load signature image to canvas if exists (will be loaded after canvas is initialized)
            if (data.signature_image) {
              // Wait for canvas to be initialized before loading image
              setTimeout(() => {
                if (canvasRef.current && data.signature_image) {
                  const canvas = canvasRef.current;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    const rect = canvas.getBoundingClientRect();
                    const displayWidth = rect.width;
                    const displayHeight = 200;
                    
                    const img = new Image();
                    img.onload = () => {
                      ctx.clearRect(0, 0, canvas.width, canvas.height);
                      
                      // Calculate scaling to fit image within canvas while maintaining aspect ratio
                      const scale = Math.min(displayWidth / img.width, displayHeight / img.height);
                      const scaledWidth = img.width * scale;
                      const scaledHeight = img.height * scale;
                      const x = (displayWidth - scaledWidth) / 2;
                      const y = (displayHeight - scaledHeight) / 2;
                      
                      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
                    };
                    img.src = data.signature_image;
                  }
                }
              }, 200);
            }
          } else {
            setError(response.error || "Failed to load check sheet");
          }
        } catch (err: any) {
          setError(err.message || "Failed to load check sheet");
        } finally {
          setLoading(false);
        }
      } else {
        // New sheet - set today's date
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        setPmDate(formattedDate);
      }
    };
    
    loadSheet();
  }, [sheetId, site]);

  const completedCount = checklistItems.filter(item => item.statusOk || item.statusNotOk).length;
  const totalCount = checklistItems.length;
  const progressPercentage = (completedCount / totalCount) * 100;

  const handleStatusChange = (id: number, isOk: boolean) => {
    setChecklistItems(items =>
      items.map(item => {
        if (item.id === id) {
          return {
            ...item,
            statusOk: isOk ? true : false,
            statusNotOk: isOk ? false : true
          };
        }
        return item;
      })
    );
  };

  const handleCorrectiveActionChange = (id: number, value: string) => {
    setChecklistItems(items =>
      items.map(item =>
        item.id === id ? { ...item, correctiveAction: value } : item
      )
    );
  };

  const handleSave = async () => {
    if (!pmDate) {
      setError("PM Date is required");
      return;
    }

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const payload = {
        pm_date: pmDate,
        make: make || "",
        location: location || "",
        rating: rating || "",
        frequency: frequency,
        remarks: remarks || "",
        engineer: engineer || "",
        tech: tech || "",
        site_incharge_name: siteIncharge || "",
        signature_image: signature || "",
        signature_type: signature ? signatureMode : undefined,
        checklist_items: checklistItems.map((item, index) => ({
          serial_number: index + 1,
          description: item.description,
          status_ok: item.statusOk,
          status_not_ok: item.statusNotOk,
          corrective_action: item.correctiveAction || "",
        })),
      };

      let response;
      if (currentSheetId) {
        // Update existing sheet
        response = await wmsPmCheckSheetApi.update(currentSheetId, payload, site || undefined);
      } else {
        // Create new sheet
        response = await wmsPmCheckSheetApi.create(payload, site || undefined);
      }

      if (response.success && response.data) {
        setCurrentSheetId(response.data.id || null);
        setIsSheetSaved(true);
        setSuccessMessage(response.message || "Sheet saved successfully!");
        
        // Update URL with ID if it's a new sheet
        if (!sheetId && response.data.id) {
          navigate(`/documentation/${site}?id=${response.data.id}`, { replace: true });
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.error || "Failed to save sheet");
      }
    } catch (err: any) {
      setError(err.message || "Failed to save sheet. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Helper function to get correct canvas coordinates
  const getCanvasCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement
  ) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  };

  // Signature Drawing Functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault(); // Prevent scrolling on touch devices
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature("");
  };

  const saveSignature = async () => {
    if (!canvasRef.current) return;
    if (!currentSheetId) {
      setError("Please save the sheet first before saving signature");
      return;
    }

    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await wmsPmCheckSheetApi.updateSignature(
        currentSheetId,
        signatureData,
        signatureMode,
        site || undefined
      );

      if (response.success) {
        setSignature(signatureData);
        setSuccessMessage("Signature saved successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.error || "Failed to save signature");
      }
    } catch (err: any) {
      setError(err.message || "Failed to save signature");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const imageData = event.target?.result as string;
      
      // If sheet is saved, upload signature immediately
      if (currentSheetId) {
        setSaving(true);
        setError("");
        setSuccessMessage("");

        try {
          const response = await wmsPmCheckSheetApi.updateSignature(
            currentSheetId,
            imageData,
            'upload',
            site || undefined
          );

          if (response.success) {
            setSignature(imageData);
            setSignatureMode('upload');
            setSuccessMessage("Signature uploaded successfully!");
            setTimeout(() => setSuccessMessage(""), 3000);
          } else {
            setError(response.error || "Failed to upload signature");
          }
        } catch (err: any) {
          setError(err.message || "Failed to upload signature");
        } finally {
          setSaving(false);
        }
      } else {
        // Just set locally if sheet not saved yet
        setSignature(imageData);
        setSignatureMode('upload');
      }
      
      // Draw image on canvas
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        // Get the display size
        const rect = canvas.getBoundingClientRect();
        const displayWidth = rect.width;
        const displayHeight = 200;
        
        const img = new Image();
        img.onload = () => {
          // Clear canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          
          // Calculate scaling to fit image within canvas while maintaining aspect ratio
          const scale = Math.min(displayWidth / img.width, displayHeight / img.height);
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          const x = (displayWidth - scaledWidth) / 2;
          const y = (displayHeight - scaledHeight) / 2;
          
          // Draw image centered and scaled
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
        };
        img.src = imageData;
      }
    };
    reader.readAsDataURL(file);
    
    // Reset input
    if (signatureFileInputRef.current) {
      signatureFileInputRef.current.value = '';
    }
  };

  // Initialize canvas on mount and resize
  useEffect(() => {
    const initCanvas = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        // Get the display size (CSS size)
        const rect = canvas.getBoundingClientRect();
        const displayWidth = rect.width;
        const displayHeight = 200; // Fixed height
        
        // Set the internal size to match display size (for high DPI displays)
        const dpr = window.devicePixelRatio || 1;
        canvas.width = displayWidth * dpr;
        canvas.height = displayHeight * dpr;
        
        // Scale the context to account for device pixel ratio
        ctx.scale(dpr, dpr);
        
        // Set the CSS size to match display size
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        
        // Set drawing style - lighter line width
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5; // Lighter line width
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    };

    // Initialize after a short delay to ensure DOM is ready
    const timer = setTimeout(initCanvas, 100);
    window.addEventListener('resize', initCanvas);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', initCanvas);
    };
  }, []);

  const handleExportPDF = () => {
    if (!currentSheetId) {
      setError("Please save the sheet first before exporting");
      return;
    }
    // TODO: Implement PDF export
    alert("PDF export functionality will be implemented soon!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 pt-16 pb-2 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-[#0066A3]" />
          <p className="text-sm text-gray-600">Loading check sheet...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="documentation-page min-h-screen bg-gray-100 pt-16 pb-2"
      style={{ fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-2 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-800">{error}</p>
            <button
              onClick={() => setError("")}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <X size={14} />
            </button>
          </div>
        )}
        {successMessage && (
          <div className="mb-2 p-2.5 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
            <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-green-800">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage("")}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Header Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-2">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-0.5">
                WMS PM CHECK SHEET - Weekly
              </h1>
              <p className="text-xs text-gray-600">
                ACSEPL - ACME CHITTORGARH SOLAR ENERGY PVT. LTD. 250MW
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportPDF}
                className="documentation-action-btn flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 rounded text-gray-700 text-xs"
                style={{ fontWeight: 600 }}
              >
                <Download size={16} />
                Export PDF
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="documentation-action-btn flex items-center gap-1.5 px-3 py-1.5 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 600 }}
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Sheet
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-medium text-gray-700">
                {completedCount}/{totalCount} items completed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-[#0066A3] h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Form Information Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-2">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Form Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                PM Date
              </label>
              <input
                type="date"
                value={pmDate}
                onChange={(e) => setPmDate(e.target.value)}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Make
              </label>
              <input
                type="text"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                placeholder="Enter make"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter location"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Rating
              </label>
              <input
                type="text"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                placeholder="Enter rating"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Frequency
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white cursor-pointer"
              >
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>
          </div>
        </div>

        {/* Checklist Table Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-2 overflow-hidden">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Checklist</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-[#0066A3] text-white">
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-sm border-r border-gray-300">
                    Sl. No.
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-sm border-r border-gray-300">
                    Description of Check Point
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-sm border-r border-gray-300" colSpan={2}>
                    Status Before PM
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-sm">
                    Corrective Action
                  </th>
                </tr>
                <tr className="bg-[#0084CF] text-white">
                  <th className="border border-gray-300 border-r border-gray-300"></th>
                  <th className="border border-gray-300 border-r border-gray-300"></th>
                  <th className="border border-gray-300 px-3 py-1.5 text-center font-medium text-sm border-r border-gray-300">
                    OK
                  </th>
                  <th className="border border-gray-300 px-3 py-1.5 text-center font-medium text-sm">
                    NOT OK
                  </th>
                  <th className="border border-gray-300"></th>
                </tr>
              </thead>
              <tbody>
                {checklistItems.map((item, index) => {
                  const itemId = item.id ?? index + 1;
                  return (
                    <tr key={itemId} className="hover:bg-gray-50 transition-colors bg-white">
                      <td className="border border-gray-300 px-3 py-2 text-center font-medium text-sm bg-gray-50 border-r border-gray-300">
                        {itemId}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm text-gray-800 border-r border-gray-300">
                        {item.description}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center border-r border-gray-300">
                        <button
                          type="button"
                          onClick={() => handleStatusChange(itemId, true)}
                          className={`w-7 h-7 border-2 rounded flex items-center justify-center transition-all cursor-pointer shadow-sm ${
                            item.statusOk
                              ? "bg-[#0066A3] border-[#0066A3] text-white"
                              : "bg-white border-gray-500 hover:border-[#0084CF] hover:bg-gray-50"
                          }`}
                        >
                          {item.statusOk && <Check size={18} className="text-white" strokeWidth={3} />}
                        </button>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleStatusChange(itemId, false)}
                          className={`w-7 h-7 border-2 rounded flex items-center justify-center transition-all cursor-pointer shadow-sm ${
                            item.statusNotOk
                              ? "bg-[#0066A3] border-[#0066A3] text-white"
                              : "bg-white border-gray-500 hover:border-[#0084CF] hover:bg-gray-50"
                          }`}
                        >
                          {item.statusNotOk && <X size={18} className="text-white" strokeWidth={3} />}
                        </button>
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        <input
                          type="text"
                          value={item.correctiveAction}
                          onChange={(e) => handleCorrectiveActionChange(itemId, e.target.value)}
                          placeholder="Enter corrective action if needed..."
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Remarks Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-2">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Remarks If any:</h2>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter remarks..."
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] resize-none bg-white"
          />
        </div>

        {/* Job Done By Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-2">
          <h2 className="text-base font-semibold text-gray-900 mb-2">JOB DONE BY</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Engineer:
              </label>
              <input
                type="text"
                value={engineer}
                onChange={(e) => setEngineer(e.target.value)}
                placeholder="Enter engineer name"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Tech:
              </label>
              <input
                type="text"
                value={tech}
                onChange={(e) => setTech(e.target.value)}
                placeholder="Enter tech name"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Site In-charge Name:
              </label>
              <input
                type="text"
                value={siteIncharge}
                onChange={(e) => setSiteIncharge(e.target.value)}
                placeholder="Enter site in-charge name"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
          </div>
        </div>

        {/* Site In-charge Signature Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Site In-charge Signature</h2>
          
          {/* Warning Message */}
          {!isSheetSaved && (
            <div className="mb-3 p-2.5 bg-yellow-50 border border-orange-200 rounded-lg flex items-start gap-2">
              <AlertTriangle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-800">
                Please save the checksheet first before uploading signature
              </p>
            </div>
          )}

          {/* Signature Mode Tabs */}
          <div className="flex gap-2 mb-3 border-b border-gray-200">
            <button
              onClick={() => setSignatureMode('upload')}
              disabled={!isSheetSaved}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                signatureMode === 'upload'
                  ? 'text-[#0066A3] border-b-2 border-[#0066A3]'
                  : 'text-gray-500 hover:text-gray-700'
              } ${!isSheetSaved ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Upload size={14} />
              Upload Image
            </button>
            <button
              onClick={() => setSignatureMode('draw')}
              disabled={!isSheetSaved}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                signatureMode === 'draw'
                  ? 'text-[#0066A3] border-b-2 border-[#0066A3]'
                  : 'text-gray-500 hover:text-gray-700'
              } ${!isSheetSaved ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Pen size={14} />
              Draw Signature
            </button>
          </div>

          {/* Signature Canvas/Upload Area */}
          {signatureMode === 'draw' ? (
            <div className="space-y-3">
              <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                className="w-full border border-gray-300 rounded-lg cursor-crosshair bg-white"
                style={{ height: '200px', touchAction: 'none' }}
              />
              <div className="flex gap-2">
                <button
                  onClick={clearSignature}
                  disabled={!isSheetSaved}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} />
                  Clear
                </button>
                <button
                  onClick={saveSignature}
                  disabled={!isSheetSaved || saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Save Signature
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Draw your signature above using your mouse or touchscreen
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                ref={signatureFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={!isSheetSaved}
                className="hidden"
              />
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                {signature ? (
                  <div className="space-y-2">
                    <img
                      src={signature}
                      alt="Signature"
                      className="max-w-full h-auto max-h-48 mx-auto border border-gray-200 rounded"
                    />
                    <button
                      onClick={() => signatureFileInputRef.current?.click()}
                      disabled={!isSheetSaved}
                      className="w-full px-3 py-2 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Change Image
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-xs text-gray-500 mb-3">No signature uploaded</p>
                    <button
                      onClick={() => signatureFileInputRef.current?.click()}
                      disabled={!isSheetSaved}
                      className="px-4 py-2 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Upload Image
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WMSPMCheckSheet;
